import AurDrawer from '../common/aur-drawer/AurDrawer';
import AurButton from '../common/aur-button/AurButton';
import AurDrawerPanelContainer from '../client-details/AurDrawerPanelContainer';
import {useEffect, useState, useRef} from 'react';
import {Box, IconButton, Typography, InputAdornment} from '@mui/material';
import {t} from 'i18next';
import {
  getDomainWhitelist,
  createDomainWhitelist,
  updateDomainWhitelist,
  deleteDomainWhitelist,
} from '../../api/ClientManagementService';
import AurPopup from '../common/aur-popup/AurPopup';
import AurAskLioDrawerHeaderButton from '../aur-ask-lio/AurAskLioDrawerHeaderButton';
import {ReactComponent as DeleteIcon} from '../../assests/svg/delete-icon.svg';
import {ReactComponent as EditIcon} from '../../assests/svg/edit-icon.svg';
import {AurTextField} from '../common/aur-text-field/AurTextField';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {AxiosError} from 'axios';
import * as psl from 'psl';

type DomainWhitelistDrawerProps = {
  shouldShow: boolean;
  clientId: string;
  clientName: string;
  setDomainWhitelistDrawerOpen: (value: boolean) => void;
  setManageItem: (value: string) => void;
};

type DomainWhitelistItem = {
  clientDomainId: string;
  clientId: string;
  domainName: string;
  isDefault: boolean;
};

/** Raw DTO from POST/PUT (camelCase or PascalCase) or nested ApiResult shapes */
type DomainWhitelistApiDto = {
  clientDomainId?: string;
  clientId?: string;
  domainName?: string;
  isDefault?: boolean;
  ClientDomainId?: string;
  ClientId?: string;
  DomainName?: string;
  IsDefault?: boolean;
  value?: DomainWhitelistApiDto;
};

/**
 * createDomainWhitelist returns axios `response.data` (ApiResult), not the full axios response.
 * GET uses full axios response, so paths differ: create → `.data`, GET list → `.data.data`.
 */
const getSavedDomainFromResponse = (apiResult?: {
  data?: DomainWhitelistApiDto;
  value?: DomainWhitelistApiDto;
}): DomainWhitelistItem | undefined => {
  const raw = apiResult?.value ?? apiResult?.data?.value ?? apiResult?.data;
  const clientDomainId = raw?.clientDomainId ?? raw?.ClientDomainId;
  const domainName = raw?.domainName ?? raw?.DomainName;
  if (!clientDomainId || !domainName) {
    return undefined;
  }

  return {
    clientDomainId: String(clientDomainId),
    clientId: String(raw?.clientId ?? raw?.ClientId ?? ''),
    domainName,
    isDefault: Boolean(raw?.isDefault ?? raw?.IsDefault ?? false),
  };
};

const DomainWhitelistDrawer = (props: DomainWhitelistDrawerProps) => {
  const [closeModal, setCloseModal] = useState<boolean>(false);
  const [domainWhitelist, setDomainWhitelist] = useState<DomainWhitelistItem[]>(
    []
  );
  const [isValidDomainInInput, setIsValidDomainInInput] = useState(false);
  const [isDomainValid, setIsDomainValid] = useState(false);
  const [isDomainUnique, setIsDomainUnique] = useState(false);
  const [domainName, setDomainName] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedDomainId, setSelectedDomainId] =
    useState<DomainWhitelistItem | null>(null);
  const [editingDomainId, setEditingDomainId] = useState<string | null>(null);
  const [editingDomainName, setEditingDomainName] = useState<string>('');
  const [isEditingDomainUnique, setIsEditingDomainUnique] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const editRef = useRef<HTMLDivElement | null>(null);
  const {showToast} = useToast();

  const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

  const inputRef = useRef<HTMLDivElement | null>(null);

  const isDomainUniqueInWhitelist = (
    domain: string,
    whitelist: DomainWhitelistItem[],
    excludeId?: string
  ): boolean => {
    const baseDomain = extractBaseDomain(domain);
    if (!baseDomain) return false;

    return !whitelist.some(
      (item) =>
        extractBaseDomain(item.domainName) === baseDomain &&
        item.clientDomainId !== excludeId
    );
  };

  const extractBaseDomain = (input: string): string | null => {
    let hostname = input.trim().toLowerCase();

    // If full email => take just the domain part
    if (hostname.includes('@')) {
      hostname = hostname.split('@')[1];
    }

    const parsed = psl.parse(hostname);
    if (parsed && typeof parsed === 'object' && 'domain' in parsed) {
      return parsed.domain;
    }
    return null;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setHasUnsavedChanges(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cancel edit if clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (editRef.current && !editRef.current.contains(event.target as Node)) {
        setEditingDomainId(null);
        setEditingDomainName('');
      }
    }

    if (editingDomainId) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDomainId]);

  // Validation for editing
  useEffect(() => {
    if (editingDomainId !== null) {
      const trimmedDomain = editingDomainName.trim().toLowerCase();

      // uniqueness check (excluding current domain from list)
      const unique = isDomainUniqueInWhitelist(
        trimmedDomain,
        domainWhitelist,
        editingDomainId
      );
      setIsEditingDomainUnique(unique);
    }
  }, [editingDomainName, editingDomainId, domainWhitelist]);

  useEffect(() => {
    if (props.shouldShow) {
      fetchDomains();
    }
  }, [props?.clientId, props.shouldShow]);

  useEffect(() => {
    const trimmedDomain = domainName.trim().toLowerCase();
    const valid = domainRegex.test(trimmedDomain);
    setIsDomainValid(valid);

    const unique = isDomainUniqueInWhitelist(trimmedDomain, domainWhitelist);
    setIsDomainUnique(unique);
    setIsValidDomainInInput(valid && unique);
  }, [domainName, domainWhitelist]);

  const clearDomainTextfield = () => {
    setDomainName('');
  };

  // Trigger edit mode for a domain
  const startEditingDomain = (item: DomainWhitelistItem) => {
    setHasUnsavedChanges(true);
    setEditingDomainId(item.clientDomainId);
    setEditingDomainName(item.domainName);
  };
  // Save changes
  const saveDomainEdit = async (clientDomainId: string) => {
    try {
      const response = await updateDomainWhitelist(
        clientDomainId,
        editingDomainName,
        props?.clientId
      );
      const saved = getSavedDomainFromResponse(response?.data);
      const updatedName = saved?.domainName ?? editingDomainName;
      setDomainWhitelist((prev) =>
        prev.map((d) =>
          d.clientDomainId === clientDomainId
            ? {...d, domainName: updatedName}
            : d
        )
      );
      showToast(t('clientDetails.domainWhitelist.toasts.editMsg'), 'success');

      setEditingDomainId(null);
      setEditingDomainName('');
    } catch (err) {
      const error = err as AxiosError;
      showToast(error.message, 'error', undefined, 'center');
      console.error('Error updating domain:', err);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await getDomainWhitelist(props?.clientId);

      // Sorting logic to show default domains and after that regular(not default) domains
      const domains = response?.data?.data || [];
      const sortedDomains = [...domains].sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;

        return a.domainName.localeCompare(b.domainName, undefined, {
          sensitivity: 'base',
        });
      });

      setDomainWhitelist(sortedDomains);
    } catch (err) {
      console.log(err);
    }
  };

  const handleDeleteDomain = async (domain: DomainWhitelistItem) => {
    try {
      await deleteDomainWhitelist(domain.clientDomainId, props?.clientId);
      setDomainWhitelist((prev) =>
        prev.filter((d) => d.clientDomainId !== domain.clientDomainId)
      );
      showToast(
        t('clientDetails.domainWhitelist.toasts.deleteMsg', {
          domainName: domain.domainName,
        }),
        'success',
        undefined,
        'center'
      );
    } catch (e) {
      console.error('Failed to delete domain:', e);
    }
  };

  const sortDomains = (domains: DomainWhitelistItem[]) =>
    [...domains].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a.domainName.localeCompare(b.domainName, undefined, {
        sensitivity: 'base',
      });
    });

  const onAddClick = async (domain: string, clientId: string) => {
    try {
      const created = await createDomainWhitelist(clientId, domain);
      const newDomain = getSavedDomainFromResponse(created);
      if (newDomain) {
        setDomainWhitelist((prev) => sortDomains([...prev, newDomain]));
      } else {
        await fetchDomains();
      }
      showToast(
        t('clientDetails.domainWhitelist.toasts.addMsg', {domainName: domain}),
        'success',
        undefined,
        'center'
      );
      clearDomainTextfield();
    } catch (err) {
      const error = err as AxiosError;
      showToast(error.message, 'error', undefined, 'center');
      console.error('Error creating domain:', err);
    }
  };

  const deleteBtnConditionCheck = (item: DomainWhitelistItem) => {
    setSelectedDomainId(item);
    setOpenDeleteModal(true); // open modal instead of deleting immediately
    return true;
  };

  const handleConfirmDelete = () => {
    if (selectedDomainId) {
      handleDeleteDomain(selectedDomainId);
    }
    setOpenDeleteModal(false);
    setSelectedDomainId(null);
  };

  const handleCancelDelete = () => {
    setOpenDeleteModal(false);
    setSelectedDomainId(null);
  };

  const cleanUpFunction = () => {
    setCloseModal(false);
    props?.setDomainWhitelistDrawerOpen(false);
    props?.setManageItem('');
    setDomainWhitelist([]);
    clearDomainTextfield();
  };

  const onCloseClick = () => {
    const hasTypedText = domainName.trim().length > 0;
    if (hasTypedText) {
      setCloseModal(true);
    } else {
      setCloseModal(false);
      cleanUpFunction();
    }
  };

  const closeModalHandler = () => {
    setCloseModal(false);
  };
  const confirmModalHandler = () => {
    cleanUpFunction();
  };
  const panelContainer = (
    <AurDrawerPanelContainer
      className="domain-whitelist-panel-container"
      panelHeader={
        <Box className="client-alert-drawer-header">
          <AurButton
            dataTestId="domain-whitelist-drawer-close"
            classNames="drawer-close-button"
            label="Close"
            type="exit"
            size="Medium"
            title="Close"
            onClick={onCloseClick}
            ariaLabel={'close panel and return to Client Details/Overview page'}
          />

          <AurAskLioDrawerHeaderButton />
        </Box>
      }
      contentHeader={<></>}
      contentBody={
        <Box className="domain-whitelist-content-body">
          <Box className="domain-panel-header">
            <Typography className="typo-text headline1_size title-color">
              {t('clientDetails.domainWhitelist.title')}
            </Typography>
            <Typography className="typo-text body3_size sub-title-color">
              {t('clientDetails.domainWhitelist.subtitle')}
            </Typography>
          </Box>
          <Box className="domain-list">
            <Typography className="typo-text body2_size title-color">
              {t('clientDetails.domainWhitelist.addEditRemove')}
            </Typography>
            <Typography className="typo-text subhead4_size adress-label">
              {t('clientDetails.domainWhitelist.address')}
            </Typography>
            <Box className="whitelist-section">
              {domainWhitelist.map((item) => (
                <Box
                  key={item.clientDomainId}
                  className="whitelist-section-item"
                  ref={
                    editingDomainId === item.clientDomainId
                      ? editRef
                      : undefined
                  }
                >
                  {editingDomainId === item.clientDomainId ? (
                    <>
                      {/* Editable text field */}
                      <AurTextField
                        value={editingDomainName}
                        ariaLabel={t(
                          'clientDetails.domainWhitelist.editDomainName'
                        )}
                        onChange={(e) => setEditingDomainName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (
                              editingDomainName.trim() &&
                              domainRegex.test(editingDomainName)
                            ) {
                              saveDomainEdit(item.clientDomainId);
                            }
                          }
                        }}
                        onBlur={() => {
                          if (editingDomainName === item.domainName) {
                            setHasUnsavedChanges(false);
                            setEditingDomainId(null);
                          }
                        }}
                        size="small"
                        className="edit-whitelist-item-textbox"
                        variant="outlined"
                      />
                      {/* Save button */}
                      <AurButton
                        classNames="save-whitelist-item-btn"
                        type="continue"
                        label="Save"
                        size="Medium"
                        onClick={() => saveDomainEdit(item.clientDomainId)}
                        disabled={
                          !editingDomainName.trim() ||
                          !domainRegex.test(editingDomainName) ||
                          !isEditingDomainUnique ||
                          editingDomainName.trim().toLowerCase() ===
                            item.domainName.trim().toLowerCase()
                        }
                      />
                    </>
                  ) : (
                    <Box className="domain-name-container">
                      {/* Static domain name */}
                      <Typography className="typo-text body2_size domain-label">
                        {item.domainName}
                      </Typography>
                      {/* Edit/Delete buttons */}
                      {!item.isDefault && (
                        <Box className="button-container">
                          <IconButton
                            aria-label={t('buttons.edit')}
                            onClick={() => startEditingDomain(item)}
                            className="icon-item-btn"
                            data-testid="edit-domain-btn"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            className="icon-item-btn"
                            data-testid="delete-domain-btn"
                            aria-label={t('buttons.delete')}
                            onClick={() => deleteBtnConditionCheck(item)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            <Typography className="typo-text body2_size title-color add-another-label">
              {t('clientDetails.domainWhitelist.addAnother')}
            </Typography>
            <Box className="add-new-whitelist-section" ref={inputRef}>
              <AurTextField
                fullWidth
                placeholder={t(
                  'clientDetails.domainWhitelist.addDomainNameLable'
                )}
                label={t('clientDetails.domainWhitelist.addDomainNameLable')}
                variant="filled"
                className="aur-textfield"
                value={domainName}
                ariaLabel={t('clientDetails.domainWhitelist.addDomainName')}
                onChange={(e) => setDomainName(e.target.value)}
                error={domainName.length > 0 && !isValidDomainInInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (isDomainValid && isDomainUnique) {
                      onAddClick(domainName, props.clientId);
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AurButton
                        classNames={'add-domain-btn'}
                        dataTestId="add-domain-btn"
                        type="continue"
                        label={t('common.add')}
                        size="Medium"
                        disabled={!(isDomainValid && isDomainUnique)}
                        onClick={() => onAddClick(domainName, props.clientId)}
                      />
                    </InputAdornment>
                  ),
                }}
                helperText={
                  domainName.length > 0 && !isValidDomainInInput
                    ? t('clientDetails.domainWhitelist.invalidDomainHelper')
                    : ''
                }
              ></AurTextField>
            </Box>
          </Box>
        </Box>
      }
    />
  );

  return (
    <>
      <div
        className="client-drawer-container"
        data-testid="client-alerts-drawer"
      >
        <AurDrawer
          className="domain-whitelist-drawer"
          open={props.shouldShow}
          onClose={() => props?.setDomainWhitelistDrawerOpen(false)}
          anchor="right"
          drawerContent={panelContainer}
        />
        {closeModal && (
          <AurPopup
            aria-label={'Unsaved Modal'}
            aria-description={t(
              'clientDetails.domainWhitelist.closeModal.areaDesctiption'
            )}
            className="inactivity-popup-modal whitelist-domain-close-modal"
            open={closeModal}
            onOk={confirmModalHandler}
            onCancel={closeModalHandler}
            popupTitle={t('dialog.areYouSure')}
            popupHeading={t('clientDetails.domainWhitelist.closeModal.heading')}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={false}
            popupContent={t(`common.cancelUploadPopup.content`)}
            labelActionCancel={t(`common.cancelUploadPopup.buttonStay`)}
            labelActionOk={t(
              'clientDetails.domainWhitelist.closeModal.closePanelLabel'
            )}
          />
        )}
        {openDeleteModal && (
          <AurPopup
            aria-label={'Delete domain Modal'}
            aria-description={t(
              'clientDetails.domainWhitelist.deleteModal.areaDesctiption'
            )}
            className="inactivity-popup-modal whitelist-domain-delete-modal"
            open={openDeleteModal}
            onOk={handleConfirmDelete}
            onCancel={handleCancelDelete}
            popupTitle={t('dialog.areYouSure')}
            popupHeading={t(
              'clientDetails.domainWhitelist.deleteModal.heading'
            )}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={true}
            popupContent={t(
              'clientDetails.domainWhitelist.deleteModal.content'
            )}
            labelActionCancel={t('buttons.cancel')}
            labelActionOk={t(
              'clientDetails.domainWhitelist.deleteModal.delete'
            )}
          />
        )}
      </div>
    </>
  );
};

export default DomainWhitelistDrawer;
